import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService, EnumValues, ModalService, EnumObjectExtended } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, of, map } from 'rxjs';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ConfirmationService, MessageService } from 'primeng/api';
import _ from 'lodash';
import { InvoiceDetailModalComponent } from '../../_modals/invoice-detail-modal/invoice-detail-modal.component';
import { Invoice } from '../../../models/invoice/invoice';
import { DeliveryNote } from '../../../models/delivery-note/delivery-note';
import { WorkOrder } from '../../../models/order/work-order';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';
import {
  DucaInformationFormGroup,
  ExporterFormGroup,
  Shipment,
  ShipmentStatus,
  ShipmentStatusCommand,
  SupportingDocumentIdsFormGroup,
  TotalValuesFormGroup,
  TransporterFormGroup,
  TransportFormGroup,
} from '../../../models/shipment/shipment';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { LotItem, InvoiceLotExtended, DeliveryNoteLotExtended } from '../../../models/inventory/lot-item';
import { CustomsData } from '../../../models/customs-data';
import { DeliveryNoteRoles, IMEXAdminGroup, IMEXEditorGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { DeliveryNoteProxyService } from '../../../proxies/order/delivery-note.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { LotsService } from '../../../services/lots.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { InventoryLotProxyService } from '../../../proxies/inventory/inventory-lot.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LotsAddModalComponent } from '../../_modals/lots-add-modal/lots-add-modal.component';
import { DUCAModalComponent } from '../../_modals/duca-modal/duca-modal.component';
import { Business } from '../../../models/finance/business';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';

export interface PurchaseOrderExtended extends PurchaseOrder {
  fieldToDisplay: string;
}

@Component({
  selector: 'app-shipment-details',
  templateUrl: './shipment-details.component.html',
  styleUrls: ['./shipment-details.component.scss'],
})
export class ShipmentDetailsComponent implements OnInit, OnDestroy {
  setOptionsForPrimeNg = setOptionsForPrimeNg;
  // UI state and data
  public isLoading = 0;
  public isUpdate = false;

  //Data Models
  public shipment: Shipment | null = null;
  public purchases: PurchaseOrder[] = [];
  public purchase: PurchaseOrder | null = null;
  public invoices: Invoice[] = [];
  public selectedInvoices: Invoice[] = [];
  public localDeletedInvoices: Invoice[] = [];
  public deliveryNotes: DeliveryNote[] = [];
  public selectedDeliveryNotes: DeliveryNote[] = [];
  public localDeletedDeliveryNotes: DeliveryNote[] = [];
  public listLots: LotItem[] = [];
  public filteredPurchaseOrders: PurchaseOrderExtended[] = [];
  public filteredDeliveryNotes: DeliveryNote[] = [];
  statuses: EnumValues = {};
  statusEnumObject?: EnumObjectExtended;
  shipmentTypes: EnumValues = {};

  // Status transition map: defines which statuses can transition to which
  private readonly statusTransitions: Map<ShipmentStatus, ShipmentStatus[]> = new Map([
    [ShipmentStatus.Draft, [ShipmentStatus.InRoute]],
    [ShipmentStatus.InRoute, [ShipmentStatus.InCustoms]],
    [ShipmentStatus.InCustoms, [ShipmentStatus.ReadyToUnload]],
    [ShipmentStatus.ReadyToUnload, [ShipmentStatus.Unloading]],
    [ShipmentStatus.Unloading, [ShipmentStatus.Completed]],
    [ShipmentStatus.Completed, []], // No transitions from Completed
    [ShipmentStatus.Cancelled, []], // No transitions from Cancelled
  ]);

  // Form controls
  public searchInvoice: FormControl<Invoice | null> = new FormControl<Invoice | null>(null);
  public searchDeliveryNote: FormControl<DeliveryNote | null> = new FormControl<DeliveryNote | null>(null);
  public status = new FormControl<number>(0, {
    nonNullable: true,
    validators: Validators.required,
  });
  public shipmentType = new FormControl<number>(0, {
    nonNullable: true,
    validators: Validators.required,
  });
  public client: FormControl<Business | null> = new FormControl<Business | null>(null, {
    nonNullable: true,
  });
  public createdDate: FormControl<string | Date | null> = new FormControl('');
  public trackingNumber = new FormControl('');
  public shipmentDate: FormControl<string | Date | null> = new FormControl('');
  public shipmentCost = new FormControl<number | null>(0);
  public insuranceCost = new FormControl<number | null>(0);
  public purchaseOrder = new FormControl<PurchaseOrderExtended | null>(null);
  public invoiceSelected = new FormControl('');
  public filteredInvoices: Invoice[] = [];
  public clientOptions: Business[] = [];

  public shipmentForm = new FormGroup({
    createdDate: this.createdDate,
    purchaseOrder: this.purchaseOrder,
    trackingNumber: this.trackingNumber,
    shipmentDate: this.shipmentDate,
    shipmentType: this.shipmentType,
    shipmentCost: this.shipmentCost,
    insuranceCost: this.insuranceCost,
  });

  // Customs data properties
  public customsData: CustomsData | null = null;
  public isLoadingCustomsData = false;
  public isDataPopulated = false; // Flag to track if data has been populated
  public originalFormValues: {
    ducaNumber: string | null;
    customsOfficeInitial: string | null;
    customsOfficeDestination: string | null;
    transportCode: string | null;
    transportName: string | null;
    driverName: string | null;
    transportUnitId: string | null;
    trailerIds: string | null;
    marchamosNumbers: string | null;
    totalValueInUSD: number | null;
    exporterName: string | null;
  } | null = null;

  public exporterForm: FormGroup<ExporterFormGroup>;
  public ducaInfoForm: FormGroup<DucaInformationFormGroup>;
  public transportForm: FormGroup<TransportFormGroup>;
  public transporterForm: FormGroup<TransporterFormGroup>;
  public totalValuesForm: FormGroup<TotalValuesFormGroup>;
  public supportingDocsForm: FormGroup<SupportingDocumentIdsFormGroup>;

  // Custom data form controls
  public ducaNumber = new FormControl('');
  public customsOfficeInitial = new FormControl('');
  public customsOfficeDestination = new FormControl('');
  public transportCode = new FormControl('');
  public transportName = new FormControl('');
  public driverName = new FormControl('');
  public transportUnitId = new FormControl('');
  public trailerIds = new FormControl('');
  public marchamosNumbers = new FormControl('');
  public totalValueInUSD = new FormControl(0);
  public exporterName = new FormControl('');

  public customsDataForm = new FormGroup({
    ducaNumber: this.ducaNumber,
    customsOfficeInitial: this.customsOfficeInitial,
    customsOfficeDestination: this.customsOfficeDestination,
    transportCode: this.transportCode,
    transportName: this.transportName,
    driverName: this.driverName,
    transportUnitId: this.transportUnitId,
    trailerIds: this.trailerIds,
    marchamosNumbers: this.marchamosNumbers,
    totalValueInUSD: this.totalValueInUSD,
    exporterName: this.exporterName,
  });

  public get showLotsButtons(): boolean {
    return (
      this.selectedInvoices.length > 0 && this.status.value !== ShipmentStatus.Unloading && this.status.value !== ShipmentStatus.Completed
    );
  }

  public get shipmentStatusOptions(): EnumValues {
    if (!this.statuses || typeof this.statuses !== 'object') return {};

    const currentStatus = this.shipment?.status ?? ShipmentStatus.Draft;

    // No changes allowed from Completed or Cancelled
    if (currentStatus === ShipmentStatus.Completed || currentStatus === ShipmentStatus.Cancelled) {
      return {};
    }

    // Get allowed next statuses based on current status
    const allowedNextStatuses = this.statusTransitions.get(currentStatus) ?? [];

    // Build the filtered options
    const filtered: EnumValues = {};

    // Always include current status so it shows as selected
    if (this.statuses[currentStatus]) {
      filtered[currentStatus] = this.statuses[currentStatus];
    }

    // Add allowed next statuses
    for (const status of allowedNextStatuses) {
      if (this.statuses[status]) {
        filtered[status] = this.statuses[status];
      }
    }

    // Add Cancelled option for Admin/Supervisor (except when already Completed)
    if (
      (AuthService.hasRole(IMEXAdminGroup.ShipmentAdmin) || AuthService.hasRole(IMEXAdminGroup.ShipmentSupervisor)) &&
      currentStatus !== ShipmentStatus.Completed
    ) {
      if (this.statuses[ShipmentStatus.Cancelled]) {
        filtered[ShipmentStatus.Cancelled] = this.statuses[ShipmentStatus.Cancelled];
      }
    }

    // Check if user has permission to change status
    const canChangeStatus =
      AuthService.hasRole(IMEXAdminGroup.ShipmentAdmin) ||
      AuthService.hasRole(IMEXAdminGroup.ShipmentSupervisor) ||
      AuthService.hasRole(IMEXEditorGroup.ShipmentEditor);

    if (!canChangeStatus) {
      return {};
    }

    return filtered;
  }

  public get hasUnsavedInvoices(): boolean {
    // Si no hay shipment o no tiene ID, significa que no se ha guardado
    // Y si hay invoices seleccionados, entonces hay invoices sin guardar
    if (!this.shipment || !this.shipment.id) {
      return this.selectedInvoices.length > 0;
    }

    // Si el shipment existe, comparar los invoices seleccionados con los guardados
    const savedInvoiceIds = this.shipment.invoiceReferences || [];
    const selectedInvoiceIds = this.selectedInvoices.map((inv) => inv.id).filter((id) => id) as string[];

    // Verificar si hay invoices seleccionados que no están en los guardados
    // o si la cantidad de invoices seleccionados es diferente a los guardados
    const hasUnsavedInvoices =
      selectedInvoiceIds.some((id) => !savedInvoiceIds.includes(id)) || selectedInvoiceIds.length !== savedInvoiceIds.length;

    return hasUnsavedInvoices;
  }

  public get addLotsTooltip(): string {
    if (this.hasUnsavedInvoices) {
      return this.translateService.instant('Save_Invoices_First');
    }
    return '';
  }

  public get allowToAddLots(): boolean {
    if (this.isConsignmentMode) {
      // For consignment with delivery notes
      if (this.selectedDeliveryNotes.length > 0) {
        return this.status.value !== ShipmentStatus.Unloading && this.status.value !== ShipmentStatus.Completed;
      }
      // For consignment without delivery notes (regular consignment)
      return (
        !_.isNil(this.client.value) && this.status.value !== ShipmentStatus.Unloading && this.status.value !== ShipmentStatus.Completed
      );
    }
    return (
      this.selectedInvoices.length > 0 &&
      this.status.value !== ShipmentStatus.Unloading &&
      this.status.value !== ShipmentStatus.Completed &&
      !this.hasUnsavedInvoices
    );
  }

  public get isConsignmentMode(): boolean {
    return this.shipmentType.value === 1; // PhysicalShipmentType.Consignment
  }

  canDelete(lot: LotItem): boolean {
    return _.isNil(lot.dateReceived);
  }

  isReceived(lot: LotItem): boolean {
    return !_.isNil(lot.dateReceived);
  }

  private _modalService = inject(ModalService);
  private _activatedRoute = inject(ActivatedRoute);
  private _router = inject(Router);
  private _shipmentService = inject(ShipmentProxyService);
  private _purchaseOrderService = inject(PurchaseOrderProxyService);
  private _invoiceService = inject(InvoiceProxyService);
  private _deliveryNoteService = inject(DeliveryNoteProxyService);
  private _workOrderService = inject(WorkOrderProxyService);
  private _lotsService = inject(LotsService);
  private inventoryLotProxyService = inject(InventoryLotProxyService);
  private enumService = inject(EnumService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private businessProxyService = inject(BusinessProxyService);
  private translateService = inject(TranslateService);
  private localeHelperService = inject(LocaleHelperService);
  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(() => {
      this.statusEnumObject = this.enumService.getEnum('ShipmentStatus', 'orderEnums');
      this.statuses = this.statusEnumObject?.values ?? {};
      this.shipmentTypes = this.enumService.getEnum('IncomingShipmentType', 'orderEnums').values;
    });
    this.exporterForm = new FormGroup<ExporterFormGroup>({
      nameOrSocialReason: new FormControl<string | null>(null),
      fiscalAddress: new FormControl<string | null>(null),
    });
    this.ducaInfoForm = new FormGroup<DucaInformationFormGroup>({
      uniqueReference: new FormControl<string | null>(null),
      ducaNumber: new FormControl<string | null>(null),
      customsOfficeInitial: new FormControl<string | null>(null),
      customsOfficeDeparture: new FormControl<string | null>(null),
      customsOfficeDestination: new FormControl<string | null>(null),
    });
    this.transportForm = new FormGroup<TransportFormGroup>({
      code: new FormControl<string | null>(null),
      name: new FormControl<string | null>(null),
    });
    this.transporterForm = new FormGroup<TransporterFormGroup>({
      driverFirstAndLastName: new FormControl<string | null>(null),
      transportUnitId: new FormControl<string | null>(null),
      trailerIds: new FormControl<string[] | null>(null),
      marchamosNumbers: new FormControl<string[] | null>(null),
    });
    this.totalValuesForm = new FormGroup<TotalValuesFormGroup>({
      totalValueInUSD: new FormControl<number | null>(null),
      exchangeRate: new FormControl<number | null>(null),
      localCurrency: new FormControl<string | null>(null),
    });
    this.supportingDocsForm = new FormGroup<SupportingDocumentIdsFormGroup>({
      supportingDocumentIds: new FormControl<string[] | null>([]),
    });
  }

  canEdit() {
    // if Admin
    if (
      AuthService.hasRole(IMEXAdminGroup.ShipmentAdmin) ||
      AuthService.hasRole(IMEXAdminGroup.ShipmentSupervisor) ||
      AuthService.hasRole(IMEXEditorGroup.ShipmentEditor)
    ) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(IMEXViewerGroup.ShipmentViewer)) {
      return false;
    }
    // Default
    return false;
  }

  public get editing(): boolean {
    return !_.isNil(this.shipment) && !_.isNil(this.shipment.id);
  }

  canEditLots() {
    //
    if (this.status.value === ShipmentStatus.Unloading || this.status.value === ShipmentStatus.Completed) {
      return false;
    }
    // if Admin
    if (AuthService.hasRole(IMEXAdminGroup.ShipmentAdmin) || AuthService.hasRole(IMEXEditorGroup.ShipmentEditor)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(IMEXViewerGroup.ShipmentViewer) || AuthService.hasRole(IMEXAdminGroup.ShipmentSupervisor)) {
      return false;
    }
    // Default
    return false;
  }

  canAddShipmentsTypeConsignment() {
    if (AuthService.hasRole(DeliveryNoteRoles.DeliveryNoteAdmin)) {
      return true;
    }
    return false;
  }

  public get shipmentTypeOptions(): EnumValues {
    if (this.canAddShipmentsTypeConsignment() === true) {
      return this.shipmentTypes;
    } else {
      const filtered: EnumValues = {};
      for (const key of Object.keys(this.shipmentTypes)) {
        if (key !== '1') {
          // Exclude Consignment type
          filtered[Number(key)] = this.shipmentTypes[Number(key)];
        }
      }
      return filtered;
    }
  }

  ngOnInit(): void {
    this._lotsService.resetState();
    this._activatedRoute.queryParams.subscribe(({ id }) => {
      if (id !== 'new') {
        this.isUpdate = true;
        this._getShipment(id);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });
    this.setInitialData();

    this.shipmentType.valueChanges.subscribe((type) => {
      this._lotsService.isConsignmentMode = type === 1;
      // Clear delivery notes if switching from consignment to standard
      if (type !== 1 && this.selectedDeliveryNotes.length > 0) {
        this.confirmationService.confirm({
          message: 'Changing to Standard shipment will remove all selected Delivery Notes. Continue?',
          accept: () => {
            this.selectedDeliveryNotes = [];
            this._lotsService.deliveryNoteLots = {};
            this._lotsService.isDeliveryNoteMode = false;
          },
          reject: () => {
            this.shipmentType.setValue(1, { emitEvent: false });
          },
        });
      }
    });

    this.searchInvoice.valueChanges.subscribe((value) => {
      if (value?.id) {
        this.addInvoiceAtList(value);
        this.isLoading++;
        this.inventoryLotProxyService.getLotsByInvoiceId(value.id, true, false).subscribe({
          next: (response) => {
            const invoiceLotExtended: InvoiceLotExtended = {
              invoice: value,
              purchaseOrder: this.purchaseOrder.value as PurchaseOrder,
              savedLots: response.data ?? [],
              localLots: [],
            };
            this.isLoading--;
            this._lotsService.addInvoiceLots([invoiceLotExtended]);
            this.searchInvoice.setValue(null, { emitEvent: false });
          },
          error: (error) => {
            const invoiceLotExtended: InvoiceLotExtended = {
              invoice: value,
              purchaseOrder: this.purchaseOrder.value as PurchaseOrder,
              savedLots: [],
              localLots: [],
            };
            this.isLoading--;
            this._lotsService.addInvoiceLots([invoiceLotExtended]);
            this.searchInvoice.setValue(null, { emitEvent: false });
            console.error(error);
          },
        });
      }
    });

    this.searchDeliveryNote.valueChanges.subscribe((value) => {
      if (value?.id) {
        if (!this.isConsignmentMode) {
          GlobalMessageUtil.error('Error', 'Delivery Notes can only be added to Consignment shipments');
          this.searchDeliveryNote.setValue(null, { emitEvent: false });
          return;
        }
        this.addDeliveryNoteAtList(value);

        // Load work orders for the delivery note
        if (value.workOrderReferences && value.workOrderReferences.length > 0) {
          this.isLoading++;
          const workOrderRequests = value.workOrderReferences.map((woRef) => this._workOrderService.getWorkOrderById(woRef));

          forkJoin(workOrderRequests).subscribe({
            next: (responses) => {
              responses.forEach((response) => {
                if (response.data) {
                  // Load lots for this delivery note - work order combination
                  const deliveryNoteLotExtended: DeliveryNoteLotExtended = {
                    deliveryNote: value,
                    workOrder: response.data,
                    savedLots: [], // TODO: Load existing lots if any
                    localLots: [],
                  };
                  this._lotsService.addDeliveryNoteLots([deliveryNoteLotExtended]);
                }
              });
              this.isLoading--;
              this.searchDeliveryNote.setValue(null, { emitEvent: false });
            },
            error: (error) => {
              this.isLoading--;
              console.error('Error loading work orders:', error);
              this.searchDeliveryNote.setValue(null, { emitEvent: false });
            },
          });
        } else {
          this.searchDeliveryNote.setValue(null, { emitEvent: false });
        }
      }
    });

    this.status.valueChanges.subscribe((status) => {
      if (!_.isNil(this.shipment) && !_.isEqual(this.shipment?.status, status)) {
        const currentStatusName = this.getShipmentStatusName(this.shipment!.status);
        const newStatusName = this.getShipmentStatusName(status);
        const isCancelling = status === ShipmentStatus.Cancelled;

        // Use different message for cancellation (irreversible action)
        const header = isCancelling
          ? this.translateService.instant('Shipment_Status_Cancel_Title', { default: 'Cancel Shipment' })
          : this.translateService.instant('Shipment_Status_Change_Title', { default: 'Confirm Status Change' });

        const message = isCancelling
          ? this.translateService.instant('Shipment_Status_Cancel_Message', {
              default:
                'Warning: This action is irreversible. Once cancelled, this shipment cannot be restored. Are you sure you want to cancel this shipment?',
            })
          : this.translateService.instant('Shipment_Status_Change_Message', {
              current: currentStatusName,
              new: newStatusName,
              default: `Are you sure you want to change the shipment status from "${currentStatusName}" to "${newStatusName}"?`,
            });

        this.confirmationService.confirm({
          header: header,
          message: message,
          icon: isCancelling ? 'pi pi-exclamation-circle' : 'pi pi-exclamation-triangle',
          acceptLabel: this.translateService.instant('Yes'),
          rejectLabel: this.translateService.instant('No'),
          acceptButtonStyleClass: isCancelling ? 'p-button-danger' : '',
          accept: () => {
            const command: ShipmentStatusCommand = {
              newStatus: status,
            };
            this.isLoading++;
            this._shipmentService.updateShipmentStatus(command, this.shipment!.id!).subscribe({
              next: (result) => {
                this.isLoading--;
                if (result.success) {
                  GlobalMessageUtil.success('Shipment Status', 'Shipment status updated successfully');
                  // Reload shipment data to reflect all changes made by the backend
                  this._refreshShipmentData();
                } else {
                  // If not successful, update local status anyway
                  this.shipment!.status = status;
                }
              },
              error: () => {
                this.isLoading--;
                this.status.setValue(this.shipment!.status, { emitEvent: false });
              },
            });
          },
          reject: () => {
            // Revert to previous status if user cancels
            this.status.setValue(this.shipment!.status, { emitEvent: false });
          },
        });
      }
    });

    if (!this.canEdit()) {
      this.shipmentForm.disable();
      this.searchInvoice.disable();
    }
  }

  ngOnDestroy(): void {
    this._lotsService.clear();
  }

  // Getters
  public get isReadyToUnload(): boolean {
    return this.status.value === 4;
  }

  // Form initialization
  public setInitialData(): void {
    this.createdDate.setValue(new Date());
  }

  getPackagingType(status: number): string {
    return this.enumService.getEnum('PackagingType', 'inventoryEnums').values[status];
  }

  getShipmentStatusName(status: number): string {
    return this.statuses[status] ?? `Status ${status}`;
  }

  public setShipmentData(shipment: Shipment): void {
    this.shipmentType.setValue(shipment.type);
    this.createdDate.setValue(shipment.createdDate ? new Date(shipment.createdDate) : new Date());
    this.trackingNumber.setValue(shipment.trackingNumber ?? 'Current number is null');
    this.shipmentDate.setValue(shipment.shipmentDate ? new Date(shipment.shipmentDate) : new Date());
    this.status.setValue(shipment.status ?? 0);
    this.shipmentType.setValue(shipment.type ?? 0);
  }

  // Invoice methods
  public openModalInvoice(invoice: Invoice): void {
    console.log('🚀 ~ ShipmentDetailsComponent ~ openModalInvoice ~ invoice:', invoice);
    this._modalService.open(InvoiceDetailModalComponent, { modalClass: 'modal_small' }, { invoice }).subscribe();
  }

  public openModalDeliveryNote(deliveryNote: DeliveryNote): void {
    console.log('🚀 ~ ShipmentDetailsComponent ~ openModalDeliveryNote ~ deliveryNote:', deliveryNote);
    // TODO: Implement delivery note detail modal if needed
    GlobalMessageUtil.info('Info', `Delivery Note: ${deliveryNote.number || deliveryNote.number}`);
  }

  public addInvoiceAtList(invoiceSelected: Invoice): void {
    const localDeletedInovice = this.localDeletedInvoices.find((i) => i.id === invoiceSelected.id);
    if (localDeletedInovice) {
      this.localDeletedInvoices = this.localDeletedInvoices.filter((i) => i.id !== invoiceSelected.id);
    }
    this.invoices = this.invoices.filter((i) => i.id !== invoiceSelected.id);
    this.selectedInvoices.push(invoiceSelected);
  }

  public clientAutoComplete(event: AutoCompleteCompleteEvent): void {
    const term = event.query.trim().toLowerCase();
    const command: BusinessSearchCommand = {
      name: term,
      code: term,
      businessTypes: [BusinessEntityType.Client],
    };
    this.businessProxyService.search(command, true).subscribe({
      next: (response) => {
        this.clientOptions = response.data?.items ?? [];
      },
      error: (error) => {
        this.clientOptions = [];
        console.error(error);
      },
    });
  }

  public purchaseOrderTypeAhead(event: AutoCompleteCompleteEvent): void {
    const term = event.query.trim();
    if (!term) {
      this.filteredPurchaseOrders = [];
      return;
    }

    this._purchaseOrderService
      .searchPurchaseOrder({ purchaseOrderNumber: term }, 1, 25)
      .pipe(
        map((response) => {
          const purchases = Array.isArray(response.data?.items) ? response.data.items : [];
          return purchases.map((purchase) => ({
            ...purchase,
            fieldToDisplay: purchase.purchaseOrderNumber ?? '',
          }));
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      )
      .subscribe((orders) => {
        this.filteredPurchaseOrders = orders;
      });
  }

  public optionsInvoices(event: AutoCompleteCompleteEvent): void {
    const term = event.query.trim().toLowerCase();

    const command = {
      invoiceNumber: term,
      providerInvoiceNumber: term,
      excludeInvoicesInShipment: true,
    };

    this._invoiceService.searchInvoice(command, 1, 25, false).subscribe({
      next: (response) => {
        let invoices = response.data?.items ?? [];

        // Filter out cancelled invoices (status = 4) and already selected invoices
        invoices = _.filter(invoices, (invoice) => {
          return invoice.status !== 4 && !this.selectedInvoices.some((i) => i.id === invoice.id);
        });

        // Add deleted invoices that match the search term (exclude cancelled)
        const regex = new RegExp(term, 'i');
        const matchingDeletedInvoices = this.localDeletedInvoices.filter(
          (i) => i.status !== 4 && (regex.test(i.providerInvoiceNumber ?? '') || regex.test(i.invoiceNumber!)),
        );

        // Merge invoices from API and deleted invoices, removing duplicates by ID
        const allInvoices = [...invoices, ...matchingDeletedInvoices];
        this.filteredInvoices = _.uniqBy(allInvoices, 'id');
      },
      error: (error) => {
        this.filteredInvoices = [];
        console.error(error);
      },
    });
  }

  public isSelected(id: string): boolean {
    return this.selectedInvoices.some((i) => i.id === id);
  }

  public removeInvoiceAtList(id: string): void {
    const invoice = this.selectedInvoices.find((i) => i.id === id);
    if (_.isNil(invoice)) {
      GlobalMessageUtil.error('Error', 'Invoice not found. Error removing invoice');
      return;
    }
    this._lotsService.removeInvoice(id);
    this.localDeletedInvoices.push(invoice);
    this.selectedInvoices = this.selectedInvoices.filter((i) => i.id !== id);
  }

  // Delivery Note methods
  public addDeliveryNoteAtList(deliveryNoteSelected: DeliveryNote): void {
    if (!this.isConsignmentMode) {
      GlobalMessageUtil.error('Error', 'Delivery Notes can only be added to Consignment shipments');
      return;
    }
    const localDeletedDeliveryNote = this.localDeletedDeliveryNotes.find((dn) => dn.id === deliveryNoteSelected.id);
    if (localDeletedDeliveryNote) {
      this.localDeletedDeliveryNotes = this.localDeletedDeliveryNotes.filter((dn) => dn.id !== deliveryNoteSelected.id);
    }
    this.deliveryNotes = this.deliveryNotes.filter((dn) => dn.id !== deliveryNoteSelected.id);

    // Load full delivery note details including work orders
    if (deliveryNoteSelected.id) {
      this._deliveryNoteService.getDeliveryNote(deliveryNoteSelected.id).subscribe({
        next: (response) => {
          const fullDeliveryNote = response.data;
          if (fullDeliveryNote) {
            console.log('Full delivery note loaded:', fullDeliveryNote);
            this.selectedDeliveryNotes.push(fullDeliveryNote);
          } else {
            this.selectedDeliveryNotes.push(deliveryNoteSelected);
          }
        },
        error: (error) => {
          console.error('Error loading delivery note details:', error);
          this.selectedDeliveryNotes.push(deliveryNoteSelected);
        },
      });
    } else {
      this.selectedDeliveryNotes.push(deliveryNoteSelected);
    }
  }

  public optionsDeliveryNotes(event: AutoCompleteCompleteEvent): void {
    const term = event.query.trim().toLowerCase();

    const command = {
      number: term,
      clientDocumentReference: term,
      excludeDeliveryNotesInShipments: true,
    };

    this._deliveryNoteService.searchDeliveryNotes(command).subscribe({
      next: (response) => {
        let deliveryNotes = response.data ?? [];
        deliveryNotes = _.filter(deliveryNotes, (dn) => {
          return !this.selectedDeliveryNotes.some((sdn) => sdn.id === dn.id);
        });

        const regex = new RegExp(term, 'i');
        const matchingDeletedDeliveryNotes = this.localDeletedDeliveryNotes.filter(
          (dn) => regex.test(dn.clientDocumentReference ?? '') || regex.test(dn.number ?? ''),
        );

        const allDeliveryNotes = [...deliveryNotes, ...matchingDeletedDeliveryNotes];
        this.filteredDeliveryNotes = _.uniqBy(allDeliveryNotes, 'id');
      },
      error: (error) => {
        this.filteredDeliveryNotes = [];
        console.error(error);
      },
    });
  }

  public isDeliveryNoteSelected(id: string): boolean {
    return this.selectedDeliveryNotes.some((dn) => dn.id === id);
  }

  public removeDeliveryNoteAtList(id: string): void {
    const deliveryNote = this.selectedDeliveryNotes.find((dn) => dn.id === id);
    if (_.isNil(deliveryNote)) {
      GlobalMessageUtil.error('Error', 'Delivery Note not found');
      return;
    }

    // Remove lots associated with this delivery note
    if (deliveryNote.workOrderReferences && deliveryNote.workOrderReferences.length > 0) {
      deliveryNote.workOrderReferences.forEach((woRef) => {
        const key = `${deliveryNote.id}-${woRef}`;
        delete this._lotsService.deliveryNoteLots[key];
      });
    }

    this.localDeletedDeliveryNotes.push(deliveryNote);
    this.selectedDeliveryNotes = this.selectedDeliveryNotes.filter((dn) => dn.id !== id);
  }

  // Lots methods

  public get loading(): boolean {
    return this._lotsService.loading || this.isLoading > 0;
  }

  public deleteLotItemConsignment(lotItemNumber: string) {
    this.isLoading++;
    this._lotsService.deleteLotItemConsignment(lotItemNumber).subscribe(() => this.isLoading--);
  }

  public deleteLots(invoiceId: string, lotIds?: string[]): void {
    const purchaseOrderId = this.purchaseOrder.value?.id;
    if (!purchaseOrderId) {
      GlobalMessageUtil.error('Error', 'Purchase order not found');
      return;
    }
    this.isLoading++;
    this._lotsService.deleteLots(invoiceId, purchaseOrderId, lotIds).subscribe({
      next: (result) => {
        this.isLoading--;
        if (result) {
          this._lotsService.getInvoiceLots(invoiceId);
          GlobalMessageUtil.success('Lot Item(s)', 'Lot items deleted successfully.');
        }
      },
    });
  }

  public onAddLotsClick(): void {
    if (this.hasUnsavedInvoices) {
      GlobalMessageUtil.warn('Save Invoices First', 'Please save the selected invoices before adding lots.');
      return;
    }
    this.openModalLots();
  }

  public showLotItemsTable(): boolean {
    if (this.hasUnsavedInvoices) {
      return false;
    }
    return this.selectedInvoices.length > 0;
  }

  public canRemoveInvoice(invoice: Invoice): boolean {
    if (!this.canEdit()) {
      return false;
    }

    const hasLotItems = this._lotsService.getInvoiceLots(invoice.id!).length > 0;

    return !hasLotItems;
  }

  public openModalLots(): void {
    if (this.isConsignmentMode) {
      if (this.selectedDeliveryNotes.length === 0) {
        GlobalMessageUtil.warn('No Delivery Notes', 'Please add delivery notes to the shipment first.');
        return;
      }
      this._modalService
        .open(
          LotsAddModalComponent,
          { modalClass: 'modal_small' },
          {
            deliveryNotes: this.selectedDeliveryNotes,
            isConsignment: this.isConsignmentMode,
          },
        )
        .subscribe({
          next: (result) => {
            if (result.action === 'save') {
              // Refresh the list of lots after saving
              console.log('Lots saved successfully');
            }
          },
        });
    } else {
      // Try to get provider reference from purchase order first, then from invoice directly
      this._modalService
        .open(
          LotsAddModalComponent,
          { modalClass: 'modal_small' },
          {
            invoices: this.selectedInvoices,
            isConsignment: this.isConsignmentMode,
          },
        )
        .subscribe({
          next: (result) => {
            if (result.action === 'save') {
              // Refresh the list of lots after saving
              console.log('Lots saved successfully');
            }
          },
        });
    }
  }
  public openDucaModal(): void {
    this._modalService
      .open(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        DUCAModalComponent as any,
        {},
        {
          ducaInfoForm: this.ducaInfoForm,
          exporterForm: this.exporterForm,
          transportForm: this.transportForm,
          transporterForm: this.transporterForm,
          totalValuesForm: this.totalValuesForm,
          supportingDocsForm: this.supportingDocsForm,
          canEdit: this.canEdit(),
        },
      )
      .subscribe({
        next: (result) => {
          if (result && result.action === 'save' && result.data) {
            const customsData = result.data as CustomsData;

            // Actualizar los valores del formulario con los datos recibidos del modal
            this.exporterName.setValue(customsData.exporter?.nameOrSocialReason || '');
            this.ducaNumber.setValue(customsData.ducaInformation?.ducaNumber || '');
            this.customsOfficeInitial.setValue(customsData.ducaInformation?.customsOfficeInitial || '');
            this.customsOfficeDestination.setValue(customsData.ducaInformation?.customsOfficeDestination || '');
            this.transportCode.setValue(customsData.transport?.code || '');
            this.transportName.setValue(customsData.transport?.name || '');
            this.driverName.setValue(customsData.transporter?.driverFirstAndLastName || '');
            this.transportUnitId.setValue(customsData.transporter?.transportUnitId || '');

            // Manejar los arrays correctamente
            if (customsData.transporter?.trailerIds && Array.isArray(customsData.transporter.trailerIds)) {
              this.trailerIds.setValue(customsData.transporter.trailerIds.join(', '));
            }

            if (customsData.transporter?.marchamosNumbers && Array.isArray(customsData.transporter.marchamosNumbers)) {
              this.marchamosNumbers.setValue(customsData.transporter.marchamosNumbers.join(', '));
            }

            this.totalValueInUSD.setValue(customsData.totalValues?.totalValueInUSD || 0);
          }
        },
      });
  }
  viewFile(presignedUrl: string) {
    //open the file in a new tab
    window.open(presignedUrl, '_blank');
  }

  getFileName(nameWithPrefix: string): string {
    const lastSlashIndex = nameWithPrefix.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      return nameWithPrefix; // No slash found, return the original string
    }
    return nameWithPrefix.substring(lastSlashIndex + 1); // Return the substring after the last slash
  }

  onFileSelected(event: Event, uploadType: 'Files' | 'PakingList' | 'Duca'): void {
    const input = event.target as HTMLInputElement;
    this._shipmentService.uploadFiles(Array.from(input.files!), this.shipment!.id!, uploadType).subscribe({
      next: (resp) => {
        if (uploadType === 'PakingList') {
          this.shipment!.packingList?.push(...resp);
        }
        if (uploadType === 'Duca') {
          this.shipment!.duca = resp[0];
        }

        if (uploadType === 'Files') {
          this.shipment!.files?.push(...resp);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Files uploaded successfully',
          life: 2500,
          key: 'main',
        });
      },
      error: (e) => {
        console.error(e);
      },
    });
  }

  // API methods

  public disableSaveLots(): boolean {
    return this._lotsService.disableSaveLotsButton();
  }

  public save(): void {
    if (!this.disableSaveLots) {
      GlobalMessageUtil.warn('Lot Items', 'Please save lot items before proceeding.');
    }
    if (this.isUpdate) {
      this.update();
      return;
    }

    // Validate customs data before continuing
    this.validateCustomsData();

    this.isLoading++;

    // Get the lot data from the LotsService
    const invoiceReferences = this.selectedInvoices.map((inv) => inv.id || '');
    const deliveryNoteReferences = this.selectedDeliveryNotes.map((dn) => dn.id || '');

    // Get customs data from form values

    // Ensure all required fields are present and properly formatted
    const sendData: Shipment = {
      status: this.status.value,
      type: this.shipmentType.value,
      clientReference: this.isConsignmentMode ? this.client.value?.id : undefined,
      createdDate: this.createdDate.value ? new Date(this.createdDate.value.toString()) : new Date(),
      modifiedDate: new Date(),
      shipmentNumber: this.shipment?.shipmentNumber || '' /* Lines 645-646 omitted */,
      trackingNumber: this.shipmentForm.value.trackingNumber ?? '',
      files: [],
      purchaseOrderReference: this.purchaseOrder.value?.id ?? '',
      purchaseOrderNumber: this.purchaseOrder.value?.purchaseOrderNumber ?? '',
      lots: [],
      invoiceReferences: this.isConsignmentMode ? [] : invoiceReferences,
      deliveryNoteReferences: this.isConsignmentMode ? deliveryNoteReferences : [],
      shipmentDate: this.shipmentDate.value ? new Date(this.shipmentDate.value.toString()) : new Date(),
      estimatedArrivalDate: new Date(),
      actualArrivalDate: new Date(),
      exporter: this.exporterForm.value,
      ducaInformation: this.ducaInfoForm.value,
      transport: this.transportForm.value,
      transporter: this.transporterForm.value,
      totalValues: this.totalValuesForm.value,
      insuranceCost: this.shipmentForm.value.insuranceCost ?? 0,
      shippingCost: this.shipmentForm.value.shipmentCost ?? 0,
    };
    this._shipmentService.createShipment(sendData).subscribe({
      next: (shipment) => {
        this._router.navigate(['shipments']);
        if (shipment) this.isLoading--;
        GlobalMessageUtil.success(
          `${this.translateService.instant('Success')}`,
          `${this.translateService.instant('Shipment_SuccessCreated_Msg', { shipment: shipment.data?.shipmentNumber })}`,
        );
      },
      error: (error) => {
        console.error('Error creating shipment:', error);
        this.isLoading--;
      },
    });
  }

  public update(): void {
    if (!this.shipment?.id) {
      return;
    }
    // Validate customs data before continuing
    this.validateCustomsData();

    this.isLoading++;

    // Get the lot data from the LotsService
    const invoiceReferences = this.selectedInvoices.map((inv) => inv.id || '');
    const deliveryNoteReferences = this.selectedDeliveryNotes.map((dn) => dn.id || '');

    // Ensure all required fields are present and properly formatted
    const sendData: Shipment = {
      id: this.shipment.id,
      status: this.status.value,
      type: this.shipment.type,
      clientReference: this.isConsignmentMode ? (this.client.value?.id ?? this.shipment.clientReference) : undefined,
      shipmentNumber: this.shipment.shipmentNumber || '', // Ensure this is never null
      createdDate: this.createdDate.value ? new Date(this.createdDate.value.toString()) : new Date(),
      trackingNumber: this.trackingNumber.value || '',
      files: this.shipment.files || [],
      duca: this.shipment.duca,
      purchaseOrderReference: this.purchaseOrder.value?.id || '',
      purchaseOrderNumber: this.purchaseOrder.value?.purchaseOrderNumber || '',
      invoiceReferences: invoiceReferences,
      deliveryNoteReferences: this.isConsignmentMode ? deliveryNoteReferences : [],
      shipmentDate: this.shipmentDate.value ? new Date(this.shipmentDate.value.toString()) : new Date(),
      lots: this.shipment.lots || [],
      estimatedArrivalDate: this.shipment.estimatedArrivalDate || new Date(),
      actualArrivalDate: this.shipment.actualArrivalDate || new Date(),
      packingList: this.shipment.packingList || [],
      // Use form values for custom data fields
      exporter: this.exporterForm.value,
      ducaInformation: this.ducaInfoForm.value,
      transport: this.transportForm.value,
      transporter: this.transporterForm.value,
      totalValues: this.totalValuesForm.value,
      insuranceCost: this.shipmentForm.value.insuranceCost ?? 0,
      shippingCost: this.shipmentForm.value.shipmentCost ?? 0,
    };

    this._shipmentService.updateShipment(sendData).subscribe({
      next: (shipment) => {
        this._router.navigate(['shipments']);
        if (shipment) this.isLoading--;
        GlobalMessageUtil.success(
          `${this.translateService.instant('Success')}`,
          `${this.translateService.instant('Shipment_SuccessUpdated_Msg', { shipment: sendData.shipmentNumber })}`,
        );
      },
      error: (error) => {
        console.error('Error updating shipment:', error);
        this.isLoading--;
      },
    });
  }

  public cancel() {
    this._router.navigate(['shipments']);
  }

  public getTotalNetQuantity(invoiceId: string): string {
    const lots = this._lotsService.getInvoiceLots(invoiceId);
    const total = _.sumBy(lots, (lot) => lot.originalNetQuantity || 0);
    return (Math.round((total + Number.EPSILON) * 100) / 100).toFixed(2);
  }

  public getTotalGrossQuantity(invoiceId: string): string {
    const lots = this._lotsService.getInvoiceLots(invoiceId);
    const total = _.sumBy(lots, (lot) => lot.originalGrossQuantity || 0);
    return (Math.round((total + Number.EPSILON) * 100) / 100).toFixed(2);
  }

  public getLotsByInvoice(invoiceId?: string): LotItem[] {
    if (!invoiceId) return [];
    return this._lotsService.getInvoiceLots(invoiceId);
  }

  public getTotalQuantitiesConsignment(): { totalNet: string; totalGross: string } {
    const lots = this._lotsService.lotItemsForConsignment;
    const totalNet = _.sumBy(lots, (lot) => lot.originalNetQuantity || 0);
    const totalGross = _.sumBy(lots, (lot) => lot.originalGrossQuantity || 0);
    return {
      totalNet: (Math.round((totalNet + Number.EPSILON) * 100) / 100).toFixed(2),
      totalGross: (Math.round((totalGross + Number.EPSILON) * 100) / 100).toFixed(2),
    };
  }

  public getConsignmentLots(): LotItem[] {
    return this._lotsService.lotItemsForConsignment;
  }

  public getPurchaseOrder(event: Event): void {
    const id = (event.target as HTMLInputElement).value;
    this._purchaseOrderService.getPurchaseOrderById(id).subscribe({
      next: (purchase) => {
        if (purchase) {
          this.purchase = purchase.data;
        }
      },
    });
  }

  public getPurchaseById(id: string): void {
    this._purchaseOrderService.getPurchaseOrderById(id).subscribe({
      next: (purchase) => {
        if (purchase) {
          this.purchase = purchase.data;
        }
      },
    });
  }

  public saveLots(): void {
    this.isLoading++;
    this._lotsService.saveLots(this.shipment?.id).subscribe({
      next: (data) => {
        this.isLoading--;
        if (data) {
          GlobalMessageUtil.success('Lot Items', 'Lot items saved successfully.');
        }
      },
      error: (error) => {
        console.error(error);
        this.isLoading--;
      },
    });
  }

  public strictLotItem(lotItem: unknown): LotItem {
    return lotItem as LotItem;
  }

  private _getShipment(id: string): void {
    this.isLoading++;

    this._shipmentService.getShipment(id).subscribe({
      next: (shipmentResponse) => {
        this.isLoading--;
        if (shipmentResponse && shipmentResponse.data) {
          const shipment: Shipment = shipmentResponse.data;
          this.shipment = shipment;
          this.breadcrumbService.setCurrent(shipment.shipmentNumber);

          if (shipment.clientReference) {
            this.client.disable();
            this.businessProxyService.getById(shipment.clientReference).subscribe({
              next: (response) => {
                this.client.setValue(response.data);
              },
            });
          }

          if (this.isShipmentConsignmentType(shipment.type)) {
            this._lotsService.initializeForShipment(shipment);
          }

          this.setShipmentData(shipment);

          this.exporterForm.patchValue({
            nameOrSocialReason: shipment.exporter?.nameOrSocialReason ?? '',
            fiscalAddress: shipment.exporter?.fiscalAddress ?? '',
          });

          this.ducaInfoForm.patchValue({
            uniqueReference: shipment.ducaInformation?.uniqueReference ?? '',
            ducaNumber: shipment.ducaInformation?.ducaNumber ?? '',
            customsOfficeInitial: shipment.ducaInformation?.customsOfficeInitial ?? '',
            customsOfficeDeparture: shipment.ducaInformation?.customsOfficeDeparture ?? '',
            customsOfficeDestination: shipment.ducaInformation?.customsOfficeDestination ?? '',
          });

          this.transportForm.patchValue({
            code: shipment.transport?.code ?? '',
            name: shipment.transport?.name ?? '',
          });

          this.transporterForm.patchValue({
            driverFirstAndLastName: shipment.transporter?.driverFirstAndLastName ?? '',
            transportUnitId: shipment.transporter?.transportUnitId ?? '',
            trailerIds: shipment.transporter?.trailerIds ?? [],
            marchamosNumbers: shipment.transporter?.marchamosNumbers ?? [],
          });
          this.totalValuesForm.patchValue({
            totalValueInUSD: shipment.totalValues?.totalValueInUSD ?? 0,
            exchangeRate: shipment.totalValues?.exchangeRate ?? 1,
            localCurrency: shipment.totalValues?.localCurrency ?? 'HNL',
          });

          this.shipmentCost.patchValue(shipment.shippingCost);
          this.insuranceCost.patchValue(shipment.insuranceCost);

          const invoicesObservables = shipment.invoiceReferences?.map((invoiceId) => this._invoiceService.getInvoice(invoiceId)) ?? [];
          if (invoicesObservables.length > 0) {
            this.isLoading++; // Start loading before fetching invoices
          }
          forkJoin([...invoicesObservables]).subscribe({
            next: ([...invoices]: ApiResponse<Invoice>[]) => {
              // Extract the invoices from ApiResponse
              const validInvoices = invoices.map((response) => response.data).filter((inv): inv is Invoice => inv !== null);

              // Set the selectedInvoices with valid invoices
              this.selectedInvoices = validInvoices;

              validInvoices.forEach((invoice) => {
                this.inventoryLotProxyService.getLotsByInvoiceId(invoice.id as string, true, false).subscribe({
                  next: (response) => {
                    const invoiceLotsExtended: InvoiceLotExtended[] = invoice.purchaseOrders?.map((po) => ({
                      invoice: invoice,
                      purchaseOrder: po,
                      savedLots: response.data?.filter(
                        (lot) => lot.invoiceReference === invoice.id && lot.purchaseOrderReference === po.id,
                      ) as LotItem[],
                      localLots: [],
                    })) as InvoiceLotExtended[];

                    // Add all invoice lots extended
                    this._lotsService.addInvoiceLots(invoiceLotsExtended);

                    // Set itemDescription to selectedInvoices
                    // if (response.data) {
                    //   response.data.forEach((element) => {
                    //     this.selectedInvoices.forEach((selectedInvoice) => {
                    //       const matchingItem = selectedInvoice.purchaseOrders?.items.find((item) => item.itemCode === element.itemCode);
                    //       if (matchingItem) {
                    //         matchingItem.itemDescription = element.item?.itemDescription;
                    //       }
                    //     });
                    //   });
                    // }

                    this.searchInvoice.setValue(null, { emitEvent: false });
                  },
                  error: (error) => {
                    const invoiceLotExtended: InvoiceLotExtended = {
                      invoice: invoice,
                      purchaseOrder: this.purchaseOrder.value as PurchaseOrder,
                      savedLots: [],
                      localLots: [],
                    };
                    this._lotsService.addInvoiceLots([invoiceLotExtended]);
                    this.searchInvoice.setValue(null, { emitEvent: false });
                    console.error(error);
                  },
                });
              });

              this.isLoading--; // End loading after processing all invoices
            },
            error: (error) => {
              console.error(error);
              this.isLoading--; // End loading if an error occurs
            },
          });

          // Load delivery notes if they exist
          const deliveryNotesObservables =
            shipment.deliveryNoteReferences?.map((dnId) => this._deliveryNoteService.getDeliveryNote(dnId)) ?? [];

          if (deliveryNotesObservables.length > 0) {
            this.isLoading++;
            forkJoin([...deliveryNotesObservables]).subscribe({
              next: ([...deliveryNotes]: ApiResponse<DeliveryNote>[]) => {
                // Extract the delivery notes from ApiResponse
                const validDeliveryNotes = deliveryNotes.map((response) => response.data).filter((dn): dn is DeliveryNote => dn !== null);

                // Set the selectedDeliveryNotes with valid delivery notes
                this.selectedDeliveryNotes = validDeliveryNotes;

                // Load work orders and lots for each delivery note
                validDeliveryNotes.forEach((deliveryNote) => {
                  if (deliveryNote.workOrderReferences && deliveryNote.workOrderReferences.length > 0) {
                    const workOrderRequests = deliveryNote.workOrderReferences.map((woRef) =>
                      this._workOrderService.getWorkOrderById(woRef),
                    );

                    forkJoin(workOrderRequests).subscribe({
                      next: (responses: ApiResponse<WorkOrder>[]) => {
                        responses.forEach((response) => {
                          if (response.data) {
                            const deliveryNoteLotExtended: DeliveryNoteLotExtended = {
                              deliveryNote: deliveryNote,
                              workOrder: response.data,
                              savedLots: [], // Lots will be loaded separately if needed
                              localLots: [],
                            };
                            this._lotsService.addDeliveryNoteLots([deliveryNoteLotExtended]);
                          }
                        });
                      },
                      error: (error) => {
                        console.error('Error loading work orders for delivery note:', error);
                      },
                    });
                  }
                });

                this.isLoading--;
              },
              error: (error) => {
                console.error(error);
                this.isLoading--;
              },
            });
          }
        }
      },
      error: (error) => {
        console.error(error);
        this.isLoading--;
      },
    });
  }

  /**
   * Refreshes the shipment data from the server to reflect all backend changes
   * (e.g., after status change, lot items cancellation, inventory transactions)
   */
  private _refreshShipmentData(): void {
    if (!this.shipment?.id) return;

    // Reset lots service state before reloading
    this._lotsService.resetState();

    // Clear current selected invoices to avoid duplicates
    this.selectedInvoices = [];

    // Reload shipment data
    this._getShipment(this.shipment.id);
  }

  public parseDate(date: Date | string, reverse = false): Date | string {
    if (!date) return '';
    if (reverse) return new Date(date);
    const parsedDate = date instanceof Date ? date : new Date(date);
    return !isNaN(parsedDate.getTime()) ? parsedDate.toISOString().split('T')[0] : '';
  }

  private isShipmentConsignmentType(type: number): boolean {
    return type === 1;
  }

  // Toggle between populating data and reverting to original state
  public toggleDataPopulation(): void {
    if (!this.isDataPopulated) {
      // Save original form values before populating
      this.originalFormValues = {
        ducaNumber: this.ducaNumber.value,
        // dateAccepted: this.dateAccepted.value,
        customsOfficeInitial: this.customsOfficeInitial.value,
        customsOfficeDestination: this.customsOfficeDestination.value,
        transportCode: this.transportCode.value,
        transportName: this.transportName.value,
        driverName: this.driverName.value,
        transportUnitId: this.transportUnitId.value,
        trailerIds: this.trailerIds.value,
        marchamosNumbers: this.marchamosNumbers.value,
        totalValueInUSD: this.totalValueInUSD.value,
        exporterName: this.exporterName.value,
      };

      // Populate the form with scrapped data
      if (this.customsData) {
        this.populateCustomsDataForm(this.customsData);
      }
      this.isDataPopulated = true;
    } else {
      // Revert to original values
      if (this.originalFormValues) {
        this.ducaNumber.setValue(this.originalFormValues.ducaNumber || '');
        // this.dateAccepted.setValue(this.originalFormValues.dateAccepted || '');
        this.customsOfficeInitial.setValue(this.originalFormValues.customsOfficeInitial || '');
        this.customsOfficeDestination.setValue(this.originalFormValues.customsOfficeDestination || '');
        this.transportCode.setValue(this.originalFormValues.transportCode || '');
        this.transportName.setValue(this.originalFormValues.transportName || '');
        this.driverName.setValue(this.originalFormValues.driverName || '');
        this.transportUnitId.setValue(this.originalFormValues.transportUnitId || '');
        this.trailerIds.setValue(this.originalFormValues.trailerIds || '');
        this.marchamosNumbers.setValue(this.originalFormValues.marchamosNumbers || '');
        this.totalValueInUSD.setValue(this.originalFormValues.totalValueInUSD || 0);
        this.exporterName.setValue(this.originalFormValues.exporterName || '');
      }
      this.isDataPopulated = false;
    }
  }

  private populateCustomsDataForm(data: CustomsData): void {
    if (!data) return;
    // DUCA Information
    this.ducaNumber.setValue(data.ducaInformation?.ducaNumber || '');
    // this.dateAccepted.setValue(data.ducaInformation?.dateAccepted || '');
    this.customsOfficeInitial.setValue(data.ducaInformation?.customsOfficeInitial || '');
    this.customsOfficeDestination.setValue(data.ducaInformation?.customsOfficeDestination || '');

    // Transport Information
    this.transportCode.setValue(data.transport?.code || '');
    this.transportName.setValue(data.transport?.name || '');

    // Transporter Information
    this.driverName.setValue(data.transporter?.driverFirstAndLastName || '');
    this.transportUnitId.setValue(data.transporter?.transportUnitId || '');
    this.trailerIds.setValue(data.transporter?.trailerIds ? data.transporter.trailerIds.join(', ') : '');
    this.marchamosNumbers.setValue(data.transporter?.marchamosNumbers ? data.transporter.marchamosNumbers.join(', ') : '');

    // Total Values
    this.totalValueInUSD.setValue(data.totalValues?.totalValueInUSD || 0);

    // Exporter Information
    this.exporterName.setValue(data.exporter?.nameOrSocialReason || '');
  }

  // Helper method to prepare customs data from form values
  private prepareCustomsDataForSaving(): CustomsData {
    return {
      exporter: {
        nameOrSocialReason: this.exporterName.value || '',
        fiscalAddress: '', // Default value as it's not in the form
      },
      ducaInformation: {
        uniqueReference: '', // Default value as it's not in the form
        ducaNumber: this.ducaNumber.value || '',
        registerDate: '',
        dateAccepted: '',
        customsOfficeInitial: this.customsOfficeInitial.value || '',
        customsOfficeDeparture: this.customsOfficeDestination.value || '', // Using destination as departure
        customsOfficeDestination: this.customsOfficeDestination.value || '',
      },
      transport: {
        code: this.transportCode.value || '',
        name: this.transportName.value || '',
        authorizationDate: '',
      },
      transporter: {
        driverFirstAndLastName: this.driverName.value || '',
        transportUnitId: this.transportUnitId.value || '',
        trailerIds: this.trailerIds.value ? this.trailerIds.value.split(',').map((id) => id.trim()) : [],
        marchamosNumbers: this.marchamosNumbers.value ? this.marchamosNumbers.value.split(',').map((id) => id.trim()) : [],
      },
      totalValues: {
        totalValueInUSD: this.totalValueInUSD.value || 0,
        exchangeRate: 1, // Default value
        localCurrency: 'HNL', // Default value
      },
      supportingDocuments: [],
      merchandise: [],
    };
  }

  // Validate customs data is present
  private validateCustomsData(): boolean {
    if (!this.ducaNumber.value) {
      console.warn('Missing DUCA Number');
    }

    if (!this.transportCode.value) {
      console.warn('Missing Transport Code');
    }

    if (!this.driverName.value) {
      console.warn('Missing Driver Name');
    }

    return true;
  }

  deleteFileShipment(indexToRemove: number, data: { name: string; presignedUrl: string }, uploadType: 'Files' | 'PakingList' | 'Duca') {
    console.log('Deleting file:', data);
    const dataFile = {
      name: data.name ?? '',
      presignedUrl: data.name,
    };

    this._shipmentService.deleteFiles([dataFile], this.shipment!.id!, uploadType).subscribe({
      next: () => {
        if (uploadType === 'PakingList') {
          this.shipment!.packingList?.splice(indexToRemove, 1);
        }
        if (uploadType === 'Duca') {
          this.shipment!.duca = undefined;
        }

        if (uploadType === 'Files') {
          this.shipment!.files?.splice(indexToRemove, 1);
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Files deleted successfully',
          life: 2500,
          key: 'main',
        });
      },
    });
  }
}
